import { AgGridReact } from "@jsq/ds/core";
import { ControlTowerContext } from "#utils/context";
import {
  ClientCellRenderer,
  SubjectCellRenderer,
  AssigneeCellRenderer,
  DueDateCellRenderer,
  StatusCellRenderer,
  StatusCategoryCellRenderer,
  WorkflowIdCellRenderer,
} from "./cellRenderers";
// Note: DescriptionInnerRenderer is used in autoGroupColumnDef in gridOptions.ts, not here.
import * as styles from "./styles_vanilla.css";
import type { WorkflowTableRow, WorkflowColumnFilterOptions } from "../types";

const formatDate = (params: { value?: string | null }): string => {
  if (!params.value) return "";
  const d = new Date(params.value);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * @param showExpanded   - true for "All open workflows" (tree mode), false for "My workflows"
 * @param filterOptions  - Available values for set-filter dropdowns (from workflowFilterOptions)
 */
export const getWorkflowColumnDefs = (
  showExpanded: boolean,
  filterOptions?: WorkflowColumnFilterOptions,
  context?: ControlTowerContext,
): AgGridReact.AgGridReactProps<WorkflowTableRow>["columnDefs"] => {
  const isGPX = context === ControlTowerContext.GPX;
  const columns: AgGridReact.AgGridReactProps<WorkflowTableRow>["columnDefs"] = [];

  // ID: shown on Canopy "My workflows" only; never shown in GPX
  if (!showExpanded && !isGPX) {
    columns.push({
      field: "workflowId",
      headerName: "ID",
      sortable: false,
      width: 140,
      checkboxSelection: true,
      headerCheckboxSelection: false,
      cellClass: styles.defaultTextCell,
      cellRenderer: WorkflowIdCellRenderer,
      filter: "agTextColumnFilter",
      suppressHeaderMenuButton: false,
      menuTabs: ["filterMenuTab"],
      filterParams: {
        filterOptions: ["contains", "equals"],
        suppressAndOrCondition: true,
        defaultOption: "contains",
      },
      valueGetter: (params) => {
        if (!params.data?.isParentRow) return "";
        return params.data.externalRefId ?? "";
      },
    });
  }

  // Description column is now the autoGroupColumnDef in gridOptions.ts.
  // It is NOT listed here to avoid a duplicate column in SSRM mode.

  // Type: Canopy only
  if (!isGPX) {
    const workflowTypeValues = (filterOptions?.workflowTypes ?? []).map((o) => o.value);
    const workflowTypeNames = Object.fromEntries(
      (filterOptions?.workflowTypes ?? []).map((o) => [o.value, o.displayName]),
    );
    columns.push({
      field: "workflowType",
      headerName: "Type",
      sortable: false,
      width: 160,
      cellClass: styles.typeCell,
      filter: "agSetColumnFilter",
      suppressHeaderMenuButton: false,
      menuTabs: ["filterMenuTab"],
      valueGetter: (params) => (params.data?.isParentRow ? params.data.workflowType : ""),
      filterParams: {
        values: workflowTypeValues,
        valueFormatter: (params: { value: string }) =>
          workflowTypeNames[params.value] ?? params.value,
      },
    });

    if (!isGPX) {
      const clientValues = (filterOptions?.clients ?? []).map((o) => o.value);
      const clientNames = Object.fromEntries(
        (filterOptions?.clients ?? []).map((o) => [o.value, o.displayName]),
      );
      columns.push({
        field: "clientName",
        headerName: "Client",
        sortable: false,
        width: 160,
        maxWidth: 240,
        flex: 1,
        cellRenderer: ClientCellRenderer,
        valueGetter: (params) => (params.data?.isParentRow ? params.data.clientId : null),
        filter: "agSetColumnFilter",
        suppressHeaderMenuButton: false,
        menuTabs: ["filterMenuTab"],
        filterParams: {
          values: clientValues,
          valueFormatter: (params: { value: string }) =>
            clientNames[params.value] ?? params.value,
        },
      });
    }
  }

  columns.push({
    field: "entityName",
    headerName: "Subject",
    sortable: false,
    width: 220,
    minWidth: 180,
    cellRenderer: SubjectCellRenderer,
    valueGetter: (params) => (params.data?.isParentRow ? params.data.entityName : null),
  });

  const parentOnly =
    (getter: (d: WorkflowTableRow) => unknown) => (params: { data?: WorkflowTableRow }) =>
      params.data?.isParentRow ? getter(params.data) : null;

  const assigneeValues = (filterOptions?.assignees ?? []).map((o) => o.value);
  const assigneeNames = Object.fromEntries(
    (filterOptions?.assignees ?? []).map((o) => [o.value, o.displayName]),
  );
  columns.push({
    field: "assignee",
    headerName: "Assignee",
    sortable: false,
    width: 180,
    cellRenderer: AssigneeCellRenderer,
    valueGetter: parentOnly((d) => d.assignee?.id ?? null),
    filter: "agSetColumnFilter",
    suppressHeaderMenuButton: false,
    menuTabs: ["filterMenuTab"],
    filterParams: {
      values: assigneeValues,
      valueFormatter: (params: { value: string }) =>
        assigneeNames[params.value] ?? params.value,
    },
  });

  // FA/IS preparer & reviewer: Canopy only — internal staff columns
  if (!isGPX) {
    columns.push({
      field: "faPreparer",
      headerName: "FA preparer",
      sortable: false,
      width: 140,
      hide: true,
      cellClass: styles.defaultTextCell,
      valueGetter: parentOnly((d) => d.faPreparer ?? ""),
    });

    columns.push({
      field: "faReviewer",
      headerName: "FA reviewer",
      sortable: false,
      width: 140,
      hide: true,
      cellClass: styles.defaultTextCell,
      valueGetter: parentOnly((d) => d.faReviewer ?? ""),
    });

    columns.push({
      field: "isPreparer",
      headerName: "IS preparer",
      sortable: false,
      width: 140,
      hide: true,
      cellClass: styles.defaultTextCell,
      valueGetter: parentOnly((d) => d.isPreparer ?? ""),
    });

    columns.push({
      field: "isReviewer",
      headerName: "IS reviewer",
      sortable: false,
      width: 140,
      hide: true,
      cellClass: styles.defaultTextCell,
      valueGetter: parentOnly((d) => d.isReviewer ?? ""),
    });
  }

  // Created: Canopy only
  if (!isGPX) {
    columns.push({
      field: "createdAt",
      headerName: "Created",
      sortable: true,
      width: 160,
      cellClass: styles.defaultTextCell,
      valueFormatter: formatDate,
      valueGetter: parentOnly((d) => d.createdAt),
    });
  }

  columns.push({
    field: "dueDate",
    headerName: "Due date",
    sortable: true,
    sort: "desc", // default sort: earliest due dates first
    width: 160,
    cellRenderer: DueDateCellRenderer,
    filter: "agDateColumnFilter",
    suppressHeaderMenuButton: false,
    menuTabs: ["filterMenuTab"],
    filterParams: {
      comparator: (filterLocalDateAtMidnight: Date, cellValue: string | null) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  });

  // Status: Canopy only — GPX shows Status Category instead
  if (!isGPX) {
    const statusValues = (filterOptions?.statuses ?? []).map((o) => o.value);
    const statusNames = Object.fromEntries(
      (filterOptions?.statuses ?? []).map((o) => [o.value, o.displayName]),
    );
    columns.push({
      field: "status",
      headerName: "Status",
      sortable: false,
      width: 220,
      cellRenderer: StatusCellRenderer,
      valueGetter: (params) => params.data?.status ?? null,
      cellClassRules: {
        // Completed task rows ("COMPLETED") and closed workflow rows ("CLOSED")
        // both get the cell-wide mint background per the wireframe — consistent
        // visual treatment for any "done" state.
        [styles.statusCellSuccess]: (params) =>
          params.data?.status === "COMPLETED" || params.data?.status === "CLOSED",
      },
      filter: "agSetColumnFilter",
      suppressHeaderMenuButton: false,
      menuTabs: ["filterMenuTab"],
      filterParams: {
        values: statusValues,
        valueFormatter: (params: { value: string }) =>
          statusNames[params.value] ?? params.value,
      },
    });
  }

  // Status Category: always shown on GPX (where it IS the primary status
  // column). On Canopy, hidden by default — Canopy already has the full Status
  // column; users can opt into Status Category via the Columns side panel.
  columns.push({
    field: "statusCategory",
    headerName: isGPX ? "Status" : "Status Category",
    sortable: false,
    width: 180,
    hide: !isGPX,
    cellRenderer: StatusCategoryCellRenderer,
    valueGetter: parentOnly((d) => d.statusCategory ?? null),
  });

  return columns;
};
